import { MIN_PERIOD_LENGTH, NOTE_SIZE, childrenOf, displayOf, effectiveTimeline, flagText, itemsExtent, mainTimelineOf, type StoryCtx, type StoryNode } from '@dispositio/core';
import cytoscape from 'cytoscape';
import { useEffect, useImperativeHandle, useRef, useState, type Ref } from 'react';
import { onSchemeChange } from '../../lib/colorScheme';
import { getAssetData, loadAsset, useAssetVersion } from '../../lib/assets';
import { cardGeometry, cardSvg, svgUri } from '../../lib/cardSvg';
import { CELL, layoutContent, type ContentLayout, type Placed } from '../../lib/contentLayout';
import { contentSvg } from '../../lib/contentSvg';
import { EVENT_SIZE, GRIP_SIZE, PERIOD_HEIGHT, UNDOCK_DISTANCE, axisValueAt, axisX, ownColor, previewSpecOf, whenBetween, lodFor, type CanvasModel, type Lod, type VNode } from '../../lib/canvasModel';
import { noteSvg } from '../../lib/noteSvg';
import { backgroundBadgeSvg, backgroundPeriodSvg, blurSvg, eventBadgeSvg, periodSvg, previewLayout, previewSvg, previewTitleSvg, tileAt as previewTileAt } from '../../lib/previewCard';
import { readTheme, type Theme } from '../../lib/theme';
import { iconFor } from '../../lib/toolIcons';
import { MAX_ZOOM, MIN_ZOOM, createWheelStepper, fitViewWithItems, zoomStep, type FitMode } from '../../lib/zoom';
import { frameCamera, nearCamera, type Box } from '../../lib/cameraFocus';
import { canvasSceneOrder, neighborsIn } from '../../lib/sceneNav';
import { timelineAt } from '../../lib/tools';
import { de } from '../../i18n/de';
import { buildStyle } from './canvasStyle';
import { ConsequenceList } from '../ConsequenceList';
import { consequenceLines, moveConsequences, type ConsequenceLine } from '../../lib/consequences';

export interface CanvasHandle {
  fit: () => void;
  zoom: () => number;
  cy: () => cytoscape.Core | null;
  /** Mitte des sichtbaren Ausschnitts in Modellkoordinaten */
  viewportCenter: () => { x: number; y: number };
}

interface Props {
  model: CanvasModel;
  ctx: StoryCtx;
  selectedId: string | null;
  /** weitere ausgewählte Szenen (Umschalt+Klick), ebenso hervorgehoben */
  alsoSelected?: readonly string[];
  selectedConnectionId: string | null;
  fitTick: number;
  /** „Alles zeigen“: der nächste fitTick passt wirklich alles ein (sonst in der Größe beim Öffnen, siehe FIT_AREA) */
  fitAll?: boolean;
  /** Fokus auf eine Szene (PF 11): weich hinfahren – gerahmt mit ihren Nachbarn bzw. nur, falls nötig */
  focus?: { id: string; mode: 'frame' | 'near'; tick: number } | null;
  /** `add`: mit Umschalttaste geklickt – zur Auswahl hinzufügen bzw. wieder herausnehmen */
  onSelect: (nodeId: string | null, add?: boolean) => void;
  onSelectConnection: (id: string) => void;
  /** Doppelklick auf einen Node */
  onOpen: (nodeId: string) => void;
  onMoveCard: (nodeId: string, pos: { x: number; y: number }) => void;
  onMoveEvent: (nodeId: string, at: number) => void;
  onMovePeriod: (nodeId: string, from: number, to: number) => void;
  /** Beschriftungsbox eines Ereignisses/Zeitraums senkrecht verschoben: neuer Abstand zum Element */
  onMoveLabel: (nodeId: string, dy: number) => void;
  /** Vorschaukarte ein- (true) bzw. aufklappen (false) */
  /** `dy`: Abstand des Zeitfähnchens zum Zeitstrahl auf der y-Achse (nur bei Zeitfähnchen) */
  onMoveFlag: (timelineId: string, flagId: string, kind: 'start' | 'end' | 'user', pos: number, dy?: number) => void;
  /**
   * Zeitlinie an ihrer Achse verschoben: neue Lage; `followers`: gekoppelte Zeitlinien, die der Hauptzeitlinie waagerecht folgen,
   * mit ihrer neuen Lage x (der Server richtet sie endgültig aus)
   */
  onMoveTimeline: (timelineId: string, pos: { x: number; y: number }, followers: { id: string; x: number }[]) => void;
  onBackgroundDoubleTap: (pos: { x: number; y: number }) => void;
  onTimelineDoubleTap: (timelineId: string, pos: number) => void;
  /** Darf der Node auf diesem Node abgelegt werden? (Rückmeldung während des Ziehens) */
  describeDrop: (dragNodeId: string, targetNodeId: string) => { ok: boolean; message: string };
  onMoveInto: (nodeId: string, targetNodeId: string, pos: { x: number; y: number }) => void;
  /** Angedocktes Ereignis bzw. angedockter Zeitraum vom Zeitstrahl weggezogen: frei auf der Fläche ablegen */
  onUndock: (nodeId: string, pos: { x: number; y: number }) => void;
  /** Notizzettel am Griff in der Größe geändert: neue Mitte und Größe (die linke obere Ecke bleibt) */
  /** Notizzettel an eine Zeitlinie heften bzw. entlang ihr verschieben (PF 14): Stelle auf der Zeitlinie, Höhe und die Ablagestelle */
  onDateNote: (nodeId: string, timelineId: string, pos: number, y: number, drop: { x: number; y: number }) => void;
  onResizeNote: (nodeId: string, box: { x: number; y: number; w: number; h: number }) => void;
  onZoomLevel?: (level: Lod) => void;
  /**
   * Ein Element wird über die Zeichenfläche hinaus gezogen (z. B. auf die Erzähllinie): liefert true, wenn der Mauszeiger über
   * einem anderen Ziel liegt; dann zeigt die Fläche keine eigene Ablage-Rückmeldung.
   */
  onDragExternal?: (nodeId: string, client: { x: number; y: number }) => boolean;
  /** Losgelassen: true, wenn ein anderes Ziel es übernommen hat; das Element springt dann an seine Stelle zurück. */
  onDropExternal?: (nodeId: string, client: { x: number; y: number }) => boolean;
  /** Mauszeiger über einem Node (Ereignis, Zeitraum, Karte, Vorschaukarte) bzw. nicht mehr (null) */
  onHoverItem?: (nodeId: string | null) => void;
  /**
   * Mauszeiger über einer Karte bzw. Vorschaukarte (Lage im Fenster) bzw. nicht mehr (null). Liefert true, wenn ein Übersichtsfenster
   * sie zeigt – dann erscheint die kleine Hover-Nachricht nicht.
   */
  onHoverPreview?: (nodeId: string | null, box: { left: number; top: number; right: number; bottom: number } | null) => boolean;
  /** Filter über den Graphen: passende Szenen (`hits`) hervorheben, die übrigen (`out`) blass; null: kein Filter */
  filter?: { hits: ReadonlySet<string>; out: ReadonlySet<string> } | null;
  handleRef?: Ref<CanvasHandle>;
}

interface TooltipState {
  x: number;
  y: number;
  title: string;
  typeName: string;
  /** Zeit des Ereignisses bzw. Zeitraums */
  when: string;
  text: string;
}

/** Durchmesser des Kreises eines leeren Nodes */
const CIRCLE_SIZE = 32;
/** Mindestbreite eines Zeitraums auf dem Bildschirm (Pixel) */
const MIN_PERIOD_PX = 56;
/** Dauer einer Kamerafahrt (Fokus auf eine Szene, „Alles zeigen“) */
const CAMERA_MS = 600;

/** Vorschaukarte eines Zeitpunkts/Zeitraums als SVG (mit den Bildern der Inhalte). */
function previewImage(v: VNode, theme: Theme, when = v.when): string {
  if (v.meta?.titleOnly) return svgUri(previewTitleSvg(v.title, theme, !!v.meta.background));
  const spec = previewSpecOf({ ...v, when }, v.meta?.show ?? { time: true, content: true, description: false });
  return svgUri(
    previewSvg(spec, previewLayout(spec), theme, (id) => {
      const data = getAssetData(id);
      if (!data) loadAsset(id);
      return data;
    }),
  );
}

/**
 * Daten, aus denen der Stylesheet (canvasStyle) die Darstellung eines Elements bestimmt. Alle Maße liegen im Modellraum: Beim Zoomen
 * wird alles gleichmäßig größer bzw. kleiner, die Darstellung selbst hängt nicht von der Zoomstufe ab.
 */
export function elementData(v: VNode, theme: Theme): { data: Record<string, unknown>; classes: string } {
  const common = { nodeId: v.nodeId, role: v.role, img: 'none', z: 5, opacity: 1, dockTl: v.meta?.docked?.timelineId ?? '', dockY: v.meta?.docked?.y ?? 0, issues: v.issues ?? 0 };
  switch (v.role) {
    case 'axis':
      return {
        // tmx: Abstand des Namens links vom Start; tmy: in die Mitte der Schienen, wo die Startmarke ihre Mitte hat
        data: { ...common, w: v.w, h: v.h, color: v.typeColor, bc: v.typeColor, bw: 2, label: v.title, fs: LABEL_PX, z: 1, tw: 220, tmx: -14, tmy: v.meta?.nameDy ?? 0 },
        classes: 'axis',
      };
    case 'flagpin':
      return { data: { ...common, w: 8, h: 8, color: theme.flagLine, bc: theme.surface, bw: 1, label: '', fs: 10, z: 2, tw: 10 }, classes: 'flagpin' };
    case 'flag': {
      const kind = v.meta?.flagKind ?? 'user';
      if (kind === 'user') {
        // Zeitfähnchen: Box, die sich dem Text (der Zeit) anpasst
        const w = Math.max(v.title.length, 3) * LABEL_PX * 0.58 + 24;
        return { data: { ...common, w, h: LABEL_PX + 14, color: theme.flagFill, bc: theme.flagLine, bw: 2, label: v.title, fs: LABEL_PX, z: 8, tw: 1000, chars: v.title.length, tc: theme.text }, classes: 'flag flag-user' };
      }
      // Start und Ende: die Zeit steht unter dem unteren Ende der Marke (bei kurzer Marke mindestens 20 unter der Achsenmitte)
      return {
        data: { ...common, w: v.w, h: v.h, color: v.typeColor, bc: theme.surface, bw: 0, label: v.title, fs: LABEL_PX, z: 2, tw: 200, tmy: Math.max(8, 20 - v.h / 2) },
        classes: `flag flag-${kind}`,
      };
    }
    case 'label': {
      // Vorschaukarte eines Zeitpunkts/Zeitraums (nur senkrecht verschiebbar)
      const own = ownColor(v);
      return {
        // ldy: Abstand zur Marke; tiley: Lage der Kachelreihe in der Karte (Pixel, -1 ohne Kacheln); für Klicks auf Kacheln und Tests
        data: { ...common, w: v.w, h: v.h, pw: v.w, ph: v.h, color: own, bc: own, bw: 0, label: '', fs: 10, z: 9, tw: 10, ldy: v.meta?.labelDy ?? 0, img: previewImage(v, theme), tiley: v.meta?.show && !v.meta.titleOnly ? (previewLayout(previewSpecOf(v, v.meta.show)).tilesY ?? -1) : -1 },
        classes: v.meta?.titleOnly ? 'preview preview-title' : 'preview',
      };
    }
    case 'note': {
      // Notizzettel: Papier in seiner Farbe (eigene, sonst die der Klasse) mit dem Text, in seiner Größe; mit Zeit hängt er an einer Zeitlinie
      const color = v.tagColor ?? v.typeColor;
      return {
        data: { ...common, w: v.w, h: v.h, color, bc: color, bw: 0, label: '', fs: 12, z: 6, tw: v.w, datedTl: v.meta?.dated?.timelineId ?? '', img: svgUri(noteSvg(v.meta?.text ?? '', color, v.w, v.h, de.notes.placeholder)) },
        classes: 'note',
      };
    }
    case 'notepin': {
      // Stelle eines Notizzettels mit Zeit auf der Achse: Marke, mit Ende ein schmaler Balken (span: seine Breite)
      const color = v.tagColor ?? v.typeColor;
      return { data: { ...common, w: v.w, h: v.h, color, bc: theme.surface, bw: 1, label: '', fs: 10, z: 2, tw: 10, span: v.w > 10 ? v.w : 0 }, classes: 'notepin' };
    }
    case 'blur': {
      // ungenaue Zeit (AP-19): blasser Bereich über die offene Spanne, hinter Marke bzw. Balken; fängt keine Maus
      const own = ownColor(v);
      return { data: { ...common, w: v.w, h: v.h, color: own, bc: own, bw: 0, label: '', fs: 10, z: 2, tw: 10, img: svgUri(blurSvg(v.w, v.h, own, theme, !!v.meta?.background || !!v.meta?.inferred)) }, classes: v.meta?.inferred ? 'blur inferred' : 'blur' };
    }
    case 'grip':
      // Griff unten rechts am gewählten Notizzettel: ändert seine Größe
      return { data: { ...common, w: GRIP_SIZE, h: GRIP_SIZE, color: theme.accent, bc: theme.surface, bw: 2, label: '', fs: 10, z: 130, tw: 10 }, classes: 'grip' };
    case 'handle': {
      // ausgewählter Zeitraum: kräftige Griffe zum Ziehen; sonst schmale Ränder, die bei Hover ihre Zeit zeigen
      const picked = v.drag !== 'none';
      const own = ownColor(v);
      return { data: { ...common, w: picked ? 10 : 8, h: picked ? 30 : 22, color: picked ? theme.accent : own, bc: picked ? theme.surface : own, bw: picked ? 2 : 1, label: '', fs: 10, z: 120, tw: 10 }, classes: picked ? 'handle handle-picked' : 'handle' };
    }
    case 'event': {
      // Zeitpunkt: runde Marke mit Pin auf der Linie; ein Hintergrundereignis (PF 15) schlicht als gestrichelter Ring
      const own = ownColor(v);
      const bg = !!v.meta?.background;
      return {
        data: { ...common, w: EVENT_SIZE, h: PERIOD_HEIGHT, color: own, bc: own, bw: 0, label: '', fs: LABEL_PX, z: 7, tw: 200, img: svgUri(bg ? backgroundBadgeSvg(own, theme, v.issues) : eventBadgeSvg(own, theme, v.issues)) },
        classes: bg ? 'event background' : 'event',
      };
    }
    case 'period': {
      // Zeitraum: leuchtender Balken mit runder Marke (Sanduhr) in der Mitte; ein Hintergrundereignis (PF 15) schmal, blass, gestrichelt
      const own = ownColor(v);
      const bg = !!v.meta?.background;
      const w = Math.max(v.w, MIN_PERIOD_PX);
      return {
        // mw: Länge auf dem Zeitstrahl; sehr kurze Zeiträume (z. B. wenige Stunden) bekommen eine Mindestbreite, damit man sie sieht und greifen kann
        data: { ...common, w, mw: v.w, h: PERIOD_HEIGHT, color: own, bc: own, bw: 0, label: '', fs: LABEL_PX, z: 3, tw: Math.max(v.w, 200), img: svgUri(bg ? backgroundPeriodSvg(w, own, theme, v.issues) : periodSvg(w, own, theme, v.issues)) },
        classes: bg ? 'period background' : 'period',
      };
    }
    default: {
      // Karte: Kreis, wenn leer, sonst Box mit Titel, Bild und Farbpunkten (auf jeder Zoomstufe dieselbe)
      if (v.empty) {
        return {
          data: { ...common, w: CIRCLE_SIZE, h: CIRCLE_SIZE, color: v.typeColor, bc: v.tagColor ?? theme.border, bw: v.tagColor ? 4 : 2, label: v.typeName, fs: LABEL_PX, tw: 170 },
          classes: 'circle',
        };
      }
      const g = cardGeometry(v, 1);
      const imageData = v.imageId ? getAssetData(v.imageId) : null;
      if (v.imageId && !imageData) loadAsset(v.imageId);
      return {
        data: { ...common, w: g.w, h: g.h, color: v.typeColor, bc: theme.border, bw: 0, label: '', fs: 12, tw: g.w, img: svgUri(cardSvg(v, 1, theme, imageData)) },
        classes: v.meta?.background ? 'card background' : 'card',
      };
    }
  }
}

/** Schriftgröße der Titel von Zeitstrahl, Fähnchen, Ereignissen und Zeiträumen (wächst und schrumpft mit dem Zoom wie alles andere) */
const LABEL_PX = 16;

/** Abstand einer Vorschaukarte zu ihrer Marke */
const previewDy = (card: cytoscape.NodeSingular): number => Number(card.data('ldy'));

/** Vorschaukarten an ihre Marken setzen und die Beschriftungen ausmessen (nach jedem Abgleich mit dem Modell). */
function relabel(cy: cytoscape.Core): void {
  cy.batch(() => {
    cy.nodes('.preview').forEach((n) => {
      const item = cy.getElementById(String(n.data('nodeId')));
      if (item.nonempty() && !n.grabbed()) n.position({ x: item.position().x, y: item.position().y + previewDy(n) });
    });
  });
  remeasureLabels(cy.nodes('.flag, .event, .period, .axis, .circle'), LABEL_PX);
}

/**
 * Cytoscape rechnet den Rahmen einer Beschriftung mit den Maßen vom letzten Zeichnen und hält ihn fest – nach einer neuen
 * Schriftgröße (Zoom) wird die Beschriftung sonst auf die alte Fläche zugeschnitten (z. B. Zeitangaben an Start und Ende nach dem
 * Wechsel des Modus). Darum: Maße für die aktuelle Schriftgröße neu berechnen und den Rahmen mit kurz geändertem Text neu messen.
 */
export function remeasureLabels(nodes: cytoscape.NodeCollection, fs: number): void {
  nodes.forEach((n) => {
    const label = String(n.data('label') ?? '');
    if (label === '' || n.scratch('_measuredFs') === fs) return;
    (n as unknown as { recalculateRenderedStyle: (useCache: boolean) => void }).recalculateRenderedStyle(false);
    n.data('label', `${label} `);
    n.data('label', label);
    n.boundingBox({ includeLabels: true });
    n.scratch('_measuredFs', fs);
  });
}

/** Abstand zwischen Node und Inhaltsstreifen darüber */
const STRIP_GAP = 10;

/**
 * Inhaltsstreifen eines Nodes (seine Kinder als Punkte, Kacheln oder Symbole, je nach Zoomstufe), links bündig über dem Node.
 * Zeitstrahle haben keinen. Liefert null, wenn der Node leer ist.
 */
function contentFor(v: VNode, owner: { w: number; h: number }, theme: Theme): { data: Record<string, unknown>; w: number; h: number; layout: ContentLayout } | null {
  if (v.kids.length === 0 || (v.role !== 'card' && v.role !== 'event' && v.role !== 'period')) return null;
  const layout = layoutContent(v.kids, 1, owner.w);
  if (!layout) return null;
  const img = contentSvg(layout, theme, (id) => {
    const data = getAssetData(id);
    if (!data) loadAsset(id);
    return data;
  });
  return { data: { nodeId: v.nodeId, role: 'content', w: layout.w, h: layout.h, img: svgUri(img), z: 6, color: '#000000', bc: '#000000', bw: 0, label: '', fs: 10, tw: 10 }, w: layout.w, h: layout.h, layout };
}

const contentPos = (owner: { x: number; y: number; w: number; h: number }, c: { w: number; h: number }): { x: number; y: number } => ({
  x: owner.x - owner.w / 2 + c.w / 2,
  y: owner.y - owner.h / 2 - STRIP_GAP - c.h / 2,
});

const signature = (v: VNode, assetVersion: number, themeKey: string): string =>
  JSON.stringify([v.role, v.title, v.hover, v.when, v.meta?.show, v.meta?.status, v.meta?.titleOnly, v.meta?.background, v.issues, v.typeColor, v.tagColor, v.chapterColor, v.imageId, v.refKey, v.imageId ? assetVersion : 0, v.empty, v.kids.map((k) => [k.id, k.typeColor, k.tagColor, k.imageId, k.title, k.glyph]), v.imageId || v.kids.some((k) => k.imageId) ? assetVersion : 0, v.w, v.h, v.meta?.flagKind, v.meta?.nameDy, v.meta?.dated?.timelineId, v.drag, themeKey]);

/** Wurde mit gedrückter Umschalttaste geklickt? */
const shiftOf = (e: cytoscape.EventObject): boolean => !!(e.originalEvent as MouseEvent | undefined)?.shiftKey;

/** Einpassen (Griffe zählen nicht mit); ohne Angabe wie zuletzt (nach „Alles zeigen“ bleibt beim Nachjustieren alles zu sehen). */
const fitCamera = (cy: cytoscape.Core, mode: FitMode = (cy.scratch('_fit') as FitMode | undefined) ?? 'view'): void => {
  cy.scratch('_fit', mode);
  fitViewWithItems(cy, cy.nodes().not('.handle'), [], mode);
};

export function StoryCanvas(props: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const cyRef = useRef<cytoscape.Core | null>(null);
  const propsRef = useRef(props);
  propsRef.current = props;
  const sigs = useRef(new Map<string, string>());
  /**
   * Zuletzt gebaute Daten je Element (Bilder der Karten als SVG): Bei unveränderter Signatur und Lage auf der Spur werden sie nicht neu
   * erzeugt – sonst baute jedes Überfahren (neues Modell) alle Karten samt eingebetteter Bilder neu, um sie dann zu verwerfen.
   */
  const built = useRef(new Map<string, { key: string; data: Record<string, unknown>; classes: string; content: ReturnType<typeof contentFor> }>());
  const firstFit = useRef(true);
  /** Fokus auf eine Szene ausführen (in der Initialisierung gesetzt); `animate`: weich hinfahren, sonst gleich dort */
  const runFocus = useRef<(f: NonNullable<Props['focus']>, animate: boolean) => void>(() => undefined);
  /** der letzte Fokus: ändert sich kurz danach die Größe der Fläche (Seitenleiste klappt auf), wird er wiederholt */
  const lastFocus = useRef<{ f: NonNullable<Props['focus']>; at: number } | null>(null);
  const lodRef = useRef<Lod>(1);
  const [theme, setTheme] = useState<Theme>(() => readTheme());
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [dropHint, setDropHint] = useState<{ ok: boolean; text: string } | null>(null);
  /** Zeitfähnchen über dem gegriffenen Endpunkt eines Zeitraums: die Zeit an der aktuellen Stelle (Bildschirmkoordinaten) */
  const [dragFlag, setDragFlag] = useState<{ x: number; y: number; text: string; edge: 'from' | 'to' } | null>(null);
  // Folgen beim Ziehen einer Szene auf der Zeitlinie (PF 55): unter ihr, was danach nicht mehr passt bzw. mitwandert
  const [consequence, setConsequence] = useState<{ x: number; y: number; lines: ConsequenceLine[] } | null>(null);
  const dropRef = useRef<{ nodeId: string; ok: boolean; pos: { x: number; y: number } } | null>(null);
  /** Zeitlinie unter einem gezogenen Notizzettel ohne Zeit (PF 14) */
  const noteDrop = useRef<string | null>(null);
  /** Platzhalter eines getragenen Ereignisses bzw. Zeitraums (folgt dem Mauszeiger, Bildschirmkoordinaten des Fensters) */
  const [carryGhost, setCarryGhost] = useState<{ x: number; y: number; kind: 'event' | 'period'; title: string; color: string } | null>(null);
  /** Kachel, die gerade aus einer Inhaltsvorschau gezogen wird (folgt dem Mauszeiger) */
  const [ghost, setGhost] = useState<{ x: number; y: number; title: string; color: string } | null>(null);
  /** Anordnung der Kacheln je Inhaltsstreifen (für Klick und Tooltip auf eine Kachel) */
  const layoutsRef = useRef(new Map<string, ContentLayout>());
  const assetVersion = useAssetVersion();

  useImperativeHandle(
    props.handleRef,
    () => ({
      fit: () => cyRef.current && fitCamera(cyRef.current, 'all'),
      zoom: () => cyRef.current?.zoom() ?? 1,
      cy: () => cyRef.current,
      viewportCenter: () => {
        const cy = cyRef.current;
        if (!cy) return { x: 0, y: 0 };
        const pan = cy.pan();
        const z = cy.zoom();
        return { x: Math.round((cy.width() / 2 - pan.x) / z), y: Math.round((cy.height() / 2 - pan.y) / z) };
      },
    }),
    [],
  );

  // --- Initialisierung
  useEffect(() => {
    if (!containerRef.current) return;
    const known = sigs.current;
    const cache = built.current;
    const cy = cytoscape({
      container: containerRef.current,
      style: buildStyle(readTheme()),
      userZoomingEnabled: false, // Zoomen mit dem Mausrad macht onWheel (kräftigere Schritte als Cytoscapes eigene Zuordnung)
      minZoom: MIN_ZOOM,
      maxZoom: MAX_ZOOM,
      boxSelectionEnabled: false,
      autounselectify: true,
      pixelRatio: 'auto',
    });
    cyRef.current = cy;
    /** Zeitraffer (PF 10): gespeicherte Stelle eines Zeitstrahls ↔ gezeigte Lage, nach dem jeweils aktuellen Modell */
    const warpOf = (tl: StoryNode) => propsRef.current.model.warps?.get(tl.id);
    const axisAt = (tl: StoryNode, x: number): number => axisValueAt(tl, x, warpOf(tl));
    const xAt = (tl: StoryNode, pos: number): number => axisX(tl, pos, warpOf(tl));
    // Test-Hook für E2E (Canvas-Inhalte sind nicht über das DOM erreichbar)
    (window as unknown as { __dpCy?: cytoscape.Core }).__dpCy = cy;

    let raf = 0;
    cy.on('zoom', () => {
      setTooltip(null);
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const next = lodFor(cy.zoom());
        if (next !== lodRef.current) {
          lodRef.current = next;
          propsRef.current.onZoomLevel?.(next);
        }
      });
    });
    cy.on('pan drag', () => setTooltip(null));

    const stepper = createWheelStepper();
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      const dir = stepper(e.deltaY, e.deltaMode);
      if (dir === 0) return;
      const rect = containerRef.current!.getBoundingClientRect();
      zoomStep(cy, dir, { x: e.clientX - rect.left, y: e.clientY - rect.top });
    };
    const wheelHost = containerRef.current;
    wheelHost.addEventListener('wheel', onWheel, { passive: false });

    // letzte Lage des Mauszeigers im Fenster (für das Ziehen über die Fläche hinaus, z. B. auf die Erzähllinie)
    const lastClient = { x: 0, y: 0 };
    const trackPointer = (e: PointerEvent | MouseEvent): void => {
      lastClient.x = e.clientX;
      lastClient.y = e.clientY;
    };
    window.addEventListener('pointermove', trackPointer, { passive: true });
    window.addEventListener('mousemove', trackPointer, { passive: true });

    const storyNode = (id: string): StoryNode | undefined => propsRef.current.ctx.nodes.get(id);
    const timelineOf = (nodeId: string): StoryNode | undefined => {
      const n = storyNode(nodeId);
      if (n?.kind === 'timeline') return n;
      const parent = n?.parentId ? storyNode(n.parentId) : undefined;
      return parent?.kind === 'timeline' ? parent : undefined;
    };

    // --- Antippen, Doppelklick
    const lastTap = new Map<string, number>();
    // Kachel im Inhaltsstreifen unter dem Mauszeiger (Modellkoordinaten)
    const tileAt = (ele: cytoscape.NodeSingular, pos: { x: number; y: number }): Placed | null => {
      const layout = layoutsRef.current.get(ele.id());
      if (!layout) return null;
      const p = ele.position();
      const lx = pos.x - (p.x - Number(ele.data('w')) / 2);
      const ly = pos.y - (p.y - Number(ele.data('h')) / 2);
      const { size, label } = CELL[layout.level];
      return layout.items.find((it) => lx >= it.x && lx <= it.x + size && ly >= it.y && ly <= it.y + size + label) ?? null;
    };
    // Kachel ziehen: Mausdruck auf einer Kachel merken, ab einigen Pixeln Bewegung wird gezogen (sonst ist es ein Klick)
    const tileDrag: { pending: { kid: Placed['kid']; startX: number; startY: number } | null; dragging: boolean; suppressUntil: number } = {
      pending: null,
      dragging: false,
      suppressUntil: 0,
    };
    cy.on('mousemove', 'node.content', (e) => {
      const hit = tileAt(e.target as cytoscape.NodeSingular, e.position);
      if (containerRef.current) containerRef.current.style.cursor = hit ? 'pointer' : '';
      if (!hit) return setTooltip(null);
      setTooltip({ x: e.renderedPosition.x + 14, y: e.renderedPosition.y + 14, title: hit.kid.title, typeName: hit.kid.typeName, when: '', text: hit.kid.hover || '–' });
    });
    cy.on('mouseout', 'node.content', () => {
      if (containerRef.current) containerRef.current.style.cursor = '';
      setTooltip(null);
    });

    cy.on('tap', 'node', (e) => {
      const ele = e.target as cytoscape.NodeSingular;
      // Vorschaukarte: Klick auf eine Kachel der Inhalte wählt dieses Kind (nur der Titel: wählt die Szene)
      if (ele.data('role') === 'label' && !ele.hasClass('preview-title')) {
        const p = ele.position();
        const w = Number(ele.data('w'));
        const h = Number(ele.data('h'));
        const v = propsRef.current.model.nodes.find((n) => n.id === ele.id());
        if (v?.meta?.show) {
          const kid = previewTileAt(previewLayout(previewSpecOf(v, v.meta.show)), e.position.x - (p.x - w / 2), e.position.y - (p.y - h / 2));
          if (kid) return propsRef.current.onSelect(kid.id);
        }
      }
      if (ele.data('role') === 'handle') {
        // ein noch nicht gewählter Zeitraum wird über seinen Rand gewählt
        if (!ele.hasClass('handle-picked')) propsRef.current.onSelect(String(ele.data('nodeId')), shiftOf(e));
        return;
      }
      // Klick auf eine Kachel der Inhaltsvorschau: dieses Kind wählen
      if (ele.data('role') === 'content') {
        if (tileDrag.dragging || Date.now() < tileDrag.suppressUntil) return; // Ende eines Ziehvorgangs, kein Klick
        const hit = tileAt(ele, e.position);
        if (hit) propsRef.current.onSelect(hit.kid.id);
        return;
      }
      const nodeId = String(ele.data('nodeId'));
      const now = Date.now();
      if (now - (lastTap.get(ele.id()) ?? 0) < 350) {
        lastTap.delete(ele.id());
        const role = ele.data('role') as string;
        if (role === 'axis') {
          const tl = storyNode(nodeId);
          if (tl) propsRef.current.onTimelineDoubleTap(nodeId, axisAt(tl, e.position.x));
        } else if (role !== 'flag' && role !== 'flagpin' && storyNode(nodeId) && propsRef.current.ctx.nodes.get(nodeId)?.kind !== 'timeline') {
          propsRef.current.onOpen(nodeId);
        }
        return;
      }
      lastTap.set(ele.id(), now);
      propsRef.current.onSelect(nodeId, shiftOf(e));
    });
    cy.on('tap', 'edge', (e) => {
      if (!e.target.hasClass('flagline')) propsRef.current.onSelectConnection(e.target.id());
    });
    let lastBgTap = 0;
    cy.on('tap', (e) => {
      if (e.target !== cy) return;
      // mit Umschalttaste daneben geklickt: die Auswahl bleibt (man verfehlt beim Zusammenstellen leicht eine Marke)
      if (!shiftOf(e)) propsRef.current.onSelect(null);
      const now = Date.now();
      if (now - lastBgTap < 350) propsRef.current.onBackgroundDoubleTap({ x: Math.round(e.position.x), y: Math.round(e.position.y) });
      lastBgTap = now;
    });

    // --- Ablegen auf einem anderen Node: Ziel unter dem Mauszeiger grün (erlaubt) bzw. rot (nicht erlaubt) markieren
    const clearDrop = (): void => {
      cy.nodes('.drop-ok, .drop-no').removeClass('drop-ok drop-no');
      dropRef.current = null;
      setDropHint(null);
    };
    const DROP_ROLES = new Set(['card', 'event', 'period', 'axis']);
    const updateDrop = (dragNodeId: string, excludeId: string | null, pos: { x: number; y: number }, ownTimelineId: string | null): void => {
      let target: cytoscape.NodeSingular | null = null;
      cy.nodes().forEach((n) => {
        if (n.id() === excludeId || !DROP_ROLES.has(String(n.data('role')))) return;
        // über der eigenen Schiene gleitet ein angedocktes Element nur entlang; abgelegt wird es dort nicht
        if (ownTimelineId && n.data('nodeId') === ownTimelineId) return;
        const bb = n.boundingBox({ includeLabels: false, includeOverlays: false, includeEdges: false });
        if (pos.x < bb.x1 || pos.x > bb.x2 || pos.y < bb.y1 || pos.y > bb.y2) return;
        if (!target || Number(n.data('z')) >= Number(target.data('z'))) target = n;
      });
      const hit = target as cytoscape.NodeSingular | null;
      cy.nodes('.drop-ok, .drop-no').removeClass('drop-ok drop-no');
      if (!hit) {
        dropRef.current = null;
        setDropHint(null);
        return;
      }
      const result = propsRef.current.describeDrop(dragNodeId, String(hit.data('nodeId')));
      hit.addClass(result.ok ? 'drop-ok' : 'drop-no');
      dropRef.current = { nodeId: String(hit.data('nodeId')), ok: result.ok, pos };
      setDropHint({ ok: result.ok, text: result.message });
    };

    // --- Kachel der Inhaltsvorschau auf einen anderen Node ziehen: das Kind wird dorthin verschoben
    cy.on('vmousedown', 'node.content', (e) => {
      const hit = tileAt(e.target as cytoscape.NodeSingular, e.position);
      if (!hit) return;
      tileDrag.pending = { kid: hit.kid, startX: e.renderedPosition.x, startY: e.renderedPosition.y };
      cy.userPanningEnabled(false); // sonst würde der Canvas mitgezogen
    });
    cy.on('vmousemove', (e) => {
      const pending = tileDrag.pending;
      if (!pending) return;
      if (!tileDrag.dragging && Math.hypot(e.renderedPosition.x - pending.startX, e.renderedPosition.y - pending.startY) < 6) return;
      tileDrag.dragging = true;
      setTooltip(null);
      if (containerRef.current) containerRef.current.style.cursor = 'grabbing';
      setGhost({ x: e.renderedPosition.x, y: e.renderedPosition.y, title: pending.kid.title, color: pending.kid.typeColor });
      updateDrop(pending.kid.id, null, e.position, null);
    });
    const endTileDrag = (): void => {
      const pending = tileDrag.pending;
      if (!pending) return;
      tileDrag.pending = null;
      cy.userPanningEnabled(true);
      if (!tileDrag.dragging) return;
      tileDrag.dragging = false;
      tileDrag.suppressUntil = Date.now() + 150;
      if (containerRef.current) containerRef.current.style.cursor = '';
      const drop = dropRef.current;
      clearDrop();
      setGhost(null);
      if (drop?.ok) propsRef.current.onMoveInto(pending.kid.id, drop.nodeId, drop.pos);
    };
    window.addEventListener('mouseup', endTileDrag);

    // Der Inhaltsstreifen folgt seinem Node
    const placeContent = (owner: cytoscape.NodeSingular): void => {
      const c = cy.getElementById(`content:${owner.id()}`);
      if (c.empty()) return;
      const p = owner.position();
      c.position(contentPos({ x: p.x, y: p.y, w: Number(owner.data('w')), h: Number(owner.data('h')) }, { w: Number(c.data('w')), h: Number(c.data('h')) }));
    };

    // Vorschaukarte: Zeit live mitführen (beim Ziehen eines Zeitpunkts, Zeitraums oder Randes)
    // Folgen der Stelle, an der eine Szene gerade gezogen wird (PF 55): gerechnet höchstens alle 90 ms, für die zuletzt erreichte Stelle
    let consequenceTimer: number | null = null;
    let consequenceAt: { nodeId: string; place: { at: number | null; from: number | null; to: number | null }; x: number; y: number } | null = null;
    const followConsequences = (nodeId: string, place: { at: number | null; from: number | null; to: number | null }, ele: cytoscape.NodeSingular): void => {
      const rp = ele.renderedPosition();
      consequenceAt = { nodeId, place, x: rp.x, y: rp.y + ele.renderedHeight() / 2 + 10 };
      if (consequenceTimer !== null) return;
      consequenceTimer = window.setTimeout(() => {
        consequenceTimer = null;
        const a = consequenceAt;
        if (!a) return;
        const ctx = propsRef.current.ctx;
        const lines = consequenceLines(ctx, moveConsequences(ctx, a.nodeId, a.place));
        setConsequence(lines.length > 0 ? { x: a.x, y: a.y, lines } : null);
      }, 90);
    };
    const stopConsequences = (): void => {
      consequenceAt = null;
      if (consequenceTimer !== null) window.clearTimeout(consequenceTimer);
      consequenceTimer = null;
      setConsequence(null);
    };

    const livePreview = (nodeId: string, when: string): void => {
      const card = cy.getElementById(`label:${nodeId}`);
      if (card.empty()) return;
      const v = propsRef.current.model.nodes.find((n) => n.id === `label:${nodeId}`);
      if (v) card.data('img', previewImage(v, readTheme(), when));
    };

    // --- Ziehen: Fähnchen bleiben auf ihrem Zeitstrahl; angedockte Ereignisse und Zeiträume folgen der Maus, gleiten beim Loslassen
    // auf ihre Spur zurück, docken an einem anderen Zeitstrahl an oder lösen sich, wenn sie weit genug weggezogen wurden.
    const snapPeriodParts = (period: cytoscape.NodeSingular, x: number, y: number): void => {
      const w = Number(period.data('w'));
      for (const edge of ['from', 'to'] as const) cy.getElementById(`handle:${period.data('nodeId')}:${edge}`).position({ x: x + (edge === 'from' ? -w / 2 : w / 2), y });
    };
    /** Element nach einem Ziehen auf ein anderes Ziel an seine Stelle zurücksetzen (samt Inhalt, Rändern, Vorschaukarte) */
    const restore = (ele: cytoscape.NodeSingular): void => {
      const v = propsRef.current.model.nodes.find((n) => n.id === ele.id());
      if (!v) return;
      if (v.role === 'label') {
        // Vorschaukarte: über ihrer Marke, Abstand in Bildschirmpixeln (wie in relabel)
        const item = cy.getElementById(v.nodeId);
        if (item.nonempty()) ele.position({ x: item.position().x, y: item.position().y + previewDy(ele) });
        return;
      }
      ele.position({ x: v.x, y: v.y });
      placeContent(ele);
      if (v.role === 'period') snapPeriodParts(ele, v.x, v.y);
      const card = cy.getElementById(`label:${v.nodeId}`);
      const lv = propsRef.current.model.nodes.find((n) => n.id === `label:${v.nodeId}`);
      if (card.nonempty() && lv) {
        card.position({ x: v.x, y: v.y + previewDy(card) });
        card.data('img', previewImage(lv, readTheme()));
      }
    };
    const EXTERNAL_ROLES = new Set(['event', 'period', 'card', 'label']);

    // --- Tragen: Wird eine Marke von ihrem Zeitstrahl weg (oder auf ein anderes Ziel wie die Erzähllinie) bzw. eine Vorschaukarte
    // seitlich aus ihrer Spalte gezogen, gleitet das Element weich an seinen Platz zurück und nur ein kleiner Platzhalter (Symbol
    // des Ereignisses bzw. Zeitraums) folgt dem Mauszeiger. Abgelegt wird an der Stelle des Mauszeigers.
    const GLIDE_MS = 220;
    const easeOut = (t: number): number => 1 - (1 - t) ** 3;
    const grabOrigin = new Map<string, { x: number; y: number }>();
    /** Größe eines Notizzettels ändern: seine linke obere Ecke, als der Griff gegriffen wurde (sie bleibt stehen) */
    const gripAnchor = new Map<string, { left: number; top: number }>();
    cy.on('grab', 'node', (e) => {
      const ele = e.target as cytoscape.NodeSingular;
      grabOrigin.set(ele.id(), { ...ele.position() });
      if (ele.data('role') === 'axis') grabLine(ele);
      // unscharfer Bereich einer ungenauen Zeit: folgt der Szene beim Ziehen im selben Abstand
      if (ele.data('role') === 'event' || ele.data('role') === 'period') {
        const blur = cy.getElementById(`blur:${String(ele.data('nodeId'))}`);
        if (blur.nonempty()) blur.scratch('_dx', blur.position().x - ele.position().x);
      }
      if (ele.data('role') === 'grip') {
        const note = cy.getElementById(String(ele.data('nodeId')));
        if (note.nonempty()) gripAnchor.set(ele.id(), { left: note.position().x - Number(note.data('w')) / 2, top: note.position().y - Number(note.data('h')) / 2 });
      }
    });
    /** Notizzettel in neuer Größe zeichnen (die linke obere Ecke bleibt) */
    const resizeNote = (note: cytoscape.NodeSingular, a: { left: number; top: number }, w: number, h: number): void => {
      const text = propsRef.current.model.nodes.find((v) => v.id === note.id())?.meta?.text ?? '';
      note.position({ x: a.left + w / 2, y: a.top + h / 2 });
      note.data({ w, h, tw: w, img: svgUri(noteSvg(text, String(note.data('color')), w, h, de.notes.placeholder)) });
    };

    // --- Zeitlinie verschieben: An ihrer Achse gegriffen zieht alles mit, was an ihr hängt (weitere Schienen, Fähnchen, Szenen, Ränder,
    // Vorschaukarten). Eine gekoppelte bewegt sich nur senkrecht; die Hauptzeitlinie nimmt gekoppelte waagerecht mit (PLAN.md §14).
    const line = { id: '', grab: { x: 0, y: 0 }, lockX: false, parts: new Map<string, { x: number; y: number; follower: string | null }>() };
    const grabLine = (axis: cytoscape.NodeSingular): void => {
      const ctx = propsRef.current.ctx;
      const id = String(axis.data('nodeId'));
      const mainId = mainTimelineOf(ctx)?.id;
      const followers = new Set(mainId === id ? childrenOf(ctx, ctx.story.rootId).filter((n) => n.kind === 'timeline' && n.timeline?.coupled).map((n) => n.id) : []);
      Object.assign(line, { id, grab: { ...axis.position() }, lockX: mainId !== id && !!ctx.nodes.get(id)?.timeline?.coupled });
      line.parts.clear();
      cy.nodes().forEach((n) => {
        const tl = String(n.data('dockTl') ?? '');
        if (n.id() !== axis.id() && (tl === id || followers.has(tl))) line.parts.set(n.id(), { ...n.position(), follower: tl === id ? null : tl });
      });
      if (containerRef.current) containerRef.current.style.cursor = 'grabbing';
    };
    const lineDelta = (axis: cytoscape.NodeSingular): { dx: number; dy: number } => {
      const p = axis.position();
      return { dx: line.lockX ? 0 : p.x - line.grab.x, dy: p.y - line.grab.y };
    };
    const dragLine = (axis: cytoscape.NodeSingular): void => {
      if (line.lockX) axis.position({ x: line.grab.x, y: axis.position().y });
      const { dx, dy } = lineDelta(axis);
      for (const [id, o] of line.parts) cy.getElementById(id).position({ x: o.x + dx, y: o.follower ? o.y : o.y + dy });
    };
    const dropLine = (axis: cytoscape.NodeSingular): void => {
      if (containerRef.current) containerRef.current.style.cursor = '';
      const tl = storyNode(line.id);
      const { dx, dy } = lineDelta(axis);
      line.id = '';
      if (!tl || (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5)) return;
      // gekoppelt bleibt x, wie der Server es ausgerichtet hat (auch krumm)
      const x = line.lockX ? (tl.x ?? 0) : Math.round((tl.x ?? 0) + dx);
      const shift = x - (tl.x ?? 0);
      const followers = [...new Set([...line.parts.values()].flatMap((o) => (o.follower ? [o.follower] : [])))];
      propsRef.current.onMoveTimeline(tl.id, { x, y: Math.round((tl.y ?? 0) + dy) }, followers.map((f) => ({ id: f, x: (storyNode(f)?.x ?? 0) + shift })));
    };
    cy.on('mouseover', 'node.axis', () => {
      if (containerRef.current && !line.id) containerRef.current.style.cursor = 'grab';
    });
    cy.on('mouseover', 'node.grip', () => {
      if (containerRef.current) containerRef.current.style.cursor = 'nwse-resize';
    });
    cy.on('mouseout', 'node.grip', () => {
      if (containerRef.current) containerRef.current.style.cursor = '';
    });
    cy.on('mouseout', 'node.axis', () => {
      if (containerRef.current && !line.id) containerRef.current.style.cursor = '';
    });
    const carry = { active: false, eleId: '', origin: { x: 0, y: 0 }, from: { x: 0, y: 0 }, start: 0, raf: 0 };
    /** Lage auf der Fläche (Modellraum) zu einer Stelle im Fenster */
    const modelAt = (c: { x: number; y: number }): { x: number; y: number } => {
      const r = containerRef.current!.getBoundingClientRect();
      const pan = cy.pan();
      const z = cy.zoom();
      return { x: (c.x - r.left - pan.x) / z, y: (c.y - r.top - pan.y) / z };
    };
    /** Teile, die einem Ereignis bzw. Zeitraum folgen (Inhaltsstreifen, Ränder, Vorschaukarte) */
    const placeParts = (ele: cytoscape.NodeSingular, role: string): void => {
      if (role === 'label') return;
      const pos = ele.position();
      placeContent(ele);
      if (role === 'period') snapPeriodParts(ele, pos.x, pos.y);
      const label = cy.getElementById(`label:${ele.data('nodeId')}`);
      if (label.nonempty() && (role === 'event' || role === 'period')) label.position({ x: pos.x, y: pos.y + previewDy(label) });
    };
    /** Das getragene Element und seine Vorschaukarte (blass, solange getragen) */
    const carriedEles = (ele: cytoscape.NodeSingular): cytoscape.CollectionReturnValue => {
      const id = String(ele.data('nodeId'));
      return cy.collection().union(ele).union(cy.getElementById(id)).union(cy.getElementById(`label:${id}`));
    };
    /** Lage des getragenen Elements: gleitet von der Stelle beim Übergang an seinen Platz zurück */
    const holdCarry = (ele: cytoscape.NodeSingular, role: string): number => {
      const k = Math.min(1, (performance.now() - carry.start) / GLIDE_MS);
      const t = easeOut(k);
      ele.position({ x: carry.from.x + (carry.origin.x - carry.from.x) * t, y: carry.from.y + (carry.origin.y - carry.from.y) * t });
      placeParts(ele, role);
      return k;
    };
    const startCarry = (ele: cytoscape.NodeSingular, role: string, nodeId: string): void => {
      cancelAnimationFrame(carry.raf);
      const origin = grabOrigin.get(ele.id()) ?? { ...ele.position() };
      // eine Vorschaukarte bewegt sich nur senkrecht: sie gleitet aus ihrer Spalte zurück, nicht von der Seite
      const from = role === 'label' ? { x: origin.x, y: ele.position().y } : { ...ele.position() };
      Object.assign(carry, { active: true, eleId: ele.id(), origin, from, start: performance.now() });
      setDragFlag(null);
      carriedEles(ele).addClass('carried');
      // die live mitgeführte Zeit der Vorschaukarte zurücksetzen
      const lv = propsRef.current.model.nodes.find((n) => n.id === `label:${nodeId}`);
      const card = cy.getElementById(`label:${nodeId}`);
      if (lv && card.nonempty()) card.data('img', previewImage(lv, readTheme()));
      const step = (): void => {
        if (!carry.active || cy.getElementById(carry.eleId).empty()) return;
        if (holdCarry(cy.getElementById(carry.eleId), role) < 1) carry.raf = requestAnimationFrame(step);
      };
      carry.raf = requestAnimationFrame(step);
    };
    const stopCarry = (ele: cytoscape.NodeSingular): void => {
      cancelAnimationFrame(carry.raf);
      carry.active = false;
      carriedEles(ele).removeClass('carried');
      setCarryGhost(null);
    };
    /** Soll das Element jetzt getragen werden (statt selbst zu folgen)? */
    const wantsCarry = (ele: cytoscape.NodeSingular, role: string, pointer: { x: number; y: number }): boolean => {
      const nodeId = String(ele.data('nodeId'));
      const kind = storyNode(nodeId)?.kind;
      if (kind !== 'event' && kind !== 'period') return false;
      if (role === 'label') {
        // seitlich aus der Spalte der Vorschaukarte gezogen (senkrecht darin: nur ihre Höhe ändern)
        const o = grabOrigin.get(ele.id()) ?? ele.position();
        const half = Number(ele.data('w')) / 2;
        return pointer.x < o.x - half || pointer.x > o.x + half;
      }
      // angedockte Marke weit genug von ihrer Spur weggezogen
      return (role === 'event' || role === 'period') && !!ele.data('dockTl') && Math.abs(pointer.y - Number(ele.data('dockY'))) > UNDOCK_DISTANCE;
    };

    cy.on('drag', 'node', (e) => {
      const ele = e.target as cytoscape.NodeSingular;
      const role = ele.data('role') as string;
      if (role === 'axis') return dragLine(ele);
      const nodeId = String(ele.data('nodeId'));
      const dockY = Number(ele.data('dockY'));
      if (EXTERNAL_ROLES.has(role)) {
        const client = { ...lastClient };
        const pointer = modelAt(client);
        // über einem anderen Ziel (Erzähllinie)? Dort gibt es die Rückmeldung, hier keine
        const overOther = !!propsRef.current.onDragExternal?.(nodeId, client);
        if (overOther || wantsCarry(ele, role, pointer)) {
          if (!carry.active || carry.eleId !== ele.id()) startCarry(ele, role, nodeId);
          holdCarry(ele, role);
          const node = storyNode(nodeId);
          if (node && (node.kind === 'event' || node.kind === 'period')) {
            const d = displayOf(propsRef.current.ctx, node);
            setCarryGhost({ x: client.x, y: client.y, kind: node.kind, title: d.title, color: ownColor(d) });
          }
          if (overOther || role === 'label' || role === 'card') clearDrop();
          else updateDrop(nodeId, ele.id(), pointer, String(ele.data('dockTl')));
          return;
        }
        if (carry.active && carry.eleId === ele.id()) {
          // zurück: das Element folgt wieder selbst dem Mauszeiger
          stopCarry(ele);
          const item = cy.getElementById(nodeId);
          ele.position(role === 'label' && item.nonempty() ? { x: item.position().x, y: pointer.y } : pointer);
        }
      }
      const p = ele.position();
      if (role === 'card') updateDrop(nodeId, ele.id(), e.position ?? p, null);
      else if (role === 'event' || role === 'period') {
        updateDrop(nodeId, ele.id(), e.position ?? p, String(ele.data('dockTl')));
        if (role === 'period') snapPeriodParts(ele, p.x, p.y);
        const blur = cy.getElementById(`blur:${nodeId}`);
        if (blur.nonempty()) blur.position({ x: ele.position().x + Number(blur.scratch('_dx') ?? 0), y: blur.position().y });
        // Zeitpunkt: Fähnchen mit der Zeit an der aktuellen Stelle, solange er am Zeitstrahl bleibt (weit weggezogen löst er sich)
        const tl = role === 'event' ? storyNode(String(ele.data('dockTl'))) : undefined;
        if (tl?.timeline && Math.abs(p.y - dockY) <= UNDOCK_DISTANCE && !dropRef.current) {
          const rp = ele.renderedPosition();
          setDragFlag({ x: rp.x, y: rp.y - ele.renderedHeight() / 2, text: flagText(effectiveTimeline(propsRef.current.ctx, tl)!, { pos: axisAt(tl, p.x), label: '' }), edge: 'from' });
        } else if (role === 'event') setDragFlag(null);
      } else if (role === 'label') {
        // nur senkrecht: x bleibt beim Element
        const item = cy.getElementById(nodeId);
        if (item.nonempty()) ele.position({ x: item.position().x, y: p.y });
      } else if (role === 'flag' && ele.hasClass('flag-user')) {
        // Zeitfähnchen: frei in x (auf dem Zeitstrahl) und y (Abstand); die Linie zum Zeitstrahl folgt
        const tl = storyNode(nodeId);
        const t = tl?.timeline;
        const x = tl && t ? Math.min(Math.max(p.x, xAt(tl, t.startPos)), xAt(tl, t.endPos)) : p.x;
        if (x !== p.x) ele.position({ x, y: p.y });
        const pin = cy.getElementById(`flagpin:${nodeId}:${String(ele.id()).split(':').slice(2).join(':')}`);
        const lanes = cy.nodes().filter((n) => n.data('role') === 'axis' && n.data('nodeId') === nodeId);
        const ys: number[] = [];
        lanes.forEach((n) => void ys.push(n.position().y));
        if (pin.nonempty() && ys.length > 0) pin.position({ x, y: Math.min(Math.max(p.y, Math.min(...ys)), Math.max(...ys)) });
      } else if (role === 'flag') {
        ele.position({ x: p.x, y: dockY });
      } else if (role === 'handle') {
        const edge = String(ele.id()).endsWith(':from') ? 'from' : 'to';
        const period = storyNode(nodeId);
        const tl = timelineOf(nodeId);
        if (!period || !tl) return;
        const desired = axisAt(tl, p.x);
        const value = edge === 'from' ? Math.min(desired, (period.to ?? 0) - MIN_PERIOD_LENGTH) : Math.max(desired, (period.from ?? 0) + MIN_PERIOD_LENGTH);
        const from = edge === 'from' ? value : (period.from ?? 0);
        const to = edge === 'to' ? value : (period.to ?? 0);
        // gezeigte Lage und Breite (mit Zeitraffer nicht proportional zur Länge auf dem Zeitstrahl)
        const [fx, tx] = [xAt(tl, from), xAt(tl, to)];
        ele.position({ x: edge === 'from' ? fx : tx, y: dockY });
        const box = cy.getElementById(period.id);
        box.position({ x: (fx + tx) / 2, y: dockY });
        box.data('w', tx - fx);
        box.data('tw', Math.max(tx - fx, 200));
        box.data({ mw: tx - fx, w: Math.max(tx - fx, MIN_PERIOD_PX), img: svgUri(periodSvg(Math.max(tx - fx, MIN_PERIOD_PX), String(box.data('color')), readTheme())) });
        placeContent(box);
        snapPeriodParts(box, box.position().x, dockY);
        const card = cy.getElementById(`label:${period.id}`);
        if (card.nonempty()) card.position({ x: box.position().x, y: dockY + previewDy(card) });
        livePreview(period.id, whenBetween(propsRef.current.ctx, tl, from, to));
        followConsequences(period.id, { at: null, from, to }, ele);
        ele.position({ x: edge === 'from' ? fx : tx, y: dockY });
        if (tl.timeline) {
          const rp = ele.renderedPosition();
          setDragFlag({ x: rp.x, y: rp.y - ele.renderedHeight() / 2, text: flagText(effectiveTimeline(propsRef.current.ctx, tl)!, { pos: value, label: '' }), edge });
        }
      } else if (role === 'note') {
        // Notizzettel liegen nur auf der Fläche (nicht in anderen Nodes); der Griff des gewählten wandert mit
        const grip = cy.getElementById(`grip:${nodeId}`);
        if (grip.nonempty()) grip.position({ x: p.x + Number(ele.data('w')) / 2, y: p.y + Number(ele.data('h')) / 2 });
        const datedTl = String(ele.data('datedTl') || '');
        const tl = datedTl ? storyNode(datedTl) : undefined;
        if (tl?.timeline) {
          // mit Zeit (PF 14): die Marke auf der Achse folgt, das Fähnchen nennt die Zeit an der Stelle
          const pin = cy.getElementById(`notepin:${nodeId}`);
          if (pin.nonempty()) pin.position({ x: p.x + Number(pin.data('span')) / 2, y: pin.position().y });
          const rp = ele.renderedPosition();
          setDragFlag({ x: rp.x, y: rp.y - ele.renderedHeight() / 2, text: flagText(effectiveTimeline(propsRef.current.ctx, tl)!, { pos: axisAt(tl, p.x), label: '' }), edge: 'from' });
        } else if (!datedTl) {
          // ohne Zeit: über einer Zeitlinie losgelassen, bekommt er dort seine Zeit
          const target = timelineAt(cy, e.position ?? p);
          noteDrop.current = target;
          const name = target ? storyNode(target) : undefined;
          setDropHint(name ? { ok: true, text: de.notes.pinTo(displayOf(propsRef.current.ctx, name).title) } : null);
        }
      } else if (role === 'grip') {
        const a = gripAnchor.get(ele.id());
        const note = cy.getElementById(nodeId);
        if (!a || note.empty()) return;
        const w = Math.max(NOTE_SIZE.minW, Math.round(p.x - a.left));
        const h = Math.max(NOTE_SIZE.minH, Math.round(p.y - a.top));
        resizeNote(note, a, w, h);
        ele.position({ x: a.left + w, y: a.top + h });
      }
      if (role === 'card' || role === 'event' || role === 'period') placeContent(ele);
      if (role === 'event' || role === 'period') {
        const label = cy.getElementById(`label:${nodeId}`);
        if (label.nonempty()) label.position({ x: ele.position().x, y: ele.position().y + previewDy(label) });
        const tlLive = storyNode(String(ele.data('dockTl')));
        const item = storyNode(nodeId);
        if (tlLive && item && Math.abs(ele.position().y - Number(ele.data('dockY'))) <= UNDOCK_DISTANCE) {
          const len = (item.to ?? 0) - (item.from ?? 0);
          // der Beginn liegt am linken Rand, so breit, wie der Zeitraum gezeigt wird (Zeitraffer)
          const start = axisAt(tlLive, role === 'event' ? ele.position().x : ele.position().x - Number(ele.data('mw') ?? ele.data('w')) / 2);
          livePreview(nodeId, role === 'event' ? whenBetween(propsRef.current.ctx, tlLive, start) : whenBetween(propsRef.current.ctx, tlLive, start, start + len));
          followConsequences(nodeId, role === 'event' ? { at: start, from: null, to: null } : { at: null, from: start, to: start + len }, ele);
        } else stopConsequences();
      }
    });
    cy.on('dragfree', 'node', (e) => {
      const ele = e.target as cytoscape.NodeSingular;
      const role = ele.data('role') as string;
      stopConsequences();
      if (role === 'axis') return dropLine(ele);
      const nodeId = String(ele.data('nodeId'));
      const p = ele.position();
      const pr = propsRef.current;
      const carried = carry.active && carry.eleId === ele.id();
      if (carried) stopCarry(ele);
      // auf einem anderen Ziel abgelegt (Erzähllinie): das Element bleibt, wo es war
      if (EXTERNAL_ROLES.has(role) && pr.onDropExternal?.(nodeId, { ...lastClient })) {
        setDragFlag(null);
        clearDrop();
        restore(ele);
        return;
      }
      if (carried) {
        // der Platzhalter wurde auf der Fläche abgelegt: Ziel unter dem Mauszeiger (in einen Node, an einen anderen Zeitstrahl)
        // oder – bei einer Marke – dort lösen; eine getragene Vorschaukarte bleibt, wo sie war
        const drop = dropRef.current;
        clearDrop();
        restore(ele);
        if (role !== 'event' && role !== 'period') return;
        if (drop?.ok) return pr.onMoveInto(nodeId, drop.nodeId, drop.pos);
        const pointer = modelAt({ ...lastClient });
        return pr.onUndock(nodeId, { x: Math.round(pointer.x), y: Math.round(pointer.y) });
      }
      if (role === 'card') {
        const drop = dropRef.current;
        clearDrop();
        if (drop?.ok) pr.onMoveInto(nodeId, drop.nodeId, drop.pos);
        else pr.onMoveCard(nodeId, { x: Math.round(p.x), y: Math.round(p.y) });
      } else if (role === 'event' || role === 'period') {
        setDragFlag(null);
        const drop = dropRef.current;
        clearDrop();
        const dockY = Number(ele.data('dockY'));
        const tl = storyNode(String(ele.data('dockTl')));
        if (drop?.ok) return pr.onMoveInto(nodeId, drop.nodeId, drop.pos);
        if (!tl) return;
        // weit genug weggezogen: vom Zeitstrahl lösen
        if (Math.abs(p.y - dockY) > UNDOCK_DISTANCE) return pr.onUndock(nodeId, { x: Math.round(p.x), y: Math.round(p.y) });
        // sonst zurück auf die Spur und entlang des Zeitstrahls verschieben
        ele.position({ x: p.x, y: dockY });
        placeContent(ele);
        if (role === 'period') snapPeriodParts(ele, p.x, dockY);
        const item = storyNode(nodeId);
        if (!item) return;
        if (role === 'event') {
          const at = axisAt(tl, p.x);
          if (at !== item.at) pr.onMoveEvent(nodeId, at);
        } else {
          const length = (item.to ?? 0) - (item.from ?? 0);
          const from = axisAt(tl, p.x - Number(ele.data('mw') ?? ele.data('w')) / 2);
          if (from !== item.from) pr.onMovePeriod(nodeId, from, from + length);
        }
      } else if (role === 'handle') {
        setDragFlag(null);
        const tl = timelineOf(nodeId);
        const period = storyNode(nodeId);
        if (!tl || !period) return;
        const edge = String(ele.id()).endsWith(':from') ? 'from' : 'to';
        const value = axisAt(tl, p.x);
        const from = edge === 'from' ? Math.min(value, (period.to ?? 0) - MIN_PERIOD_LENGTH) : (period.from ?? 0);
        const to = edge === 'to' ? Math.max(value, (period.from ?? 0) + MIN_PERIOD_LENGTH) : (period.to ?? 0);
        if (from !== period.from || to !== period.to) pr.onMovePeriod(nodeId, from, to);
      } else if (role === 'note') {
        setDragFlag(null);
        setDropHint(null);
        // mit Zeit: entlang seiner Zeitlinie (die Zeit an der Stelle, die Höhe frei); ohne Zeit auf eine Zeitlinie: angeheftet, über der Achse
        const datedTl = String(ele.data('datedTl') || '');
        const target = datedTl || noteDrop.current;
        noteDrop.current = null;
        const tl = target ? storyNode(target) : undefined;
        if (tl?.timeline) return pr.onDateNote(nodeId, tl.id, axisAt(tl, p.x), datedTl ? Math.round(p.y) : Math.round((tl.y ?? 0) - Number(ele.data('h')) / 2 - 70), { x: Math.round(p.x), y: Math.round(p.y) });
        pr.onMoveCard(nodeId, { x: Math.round(p.x), y: Math.round(p.y) });
      } else if (role === 'grip') {
        gripAnchor.delete(ele.id());
        const note = cy.getElementById(nodeId);
        if (note.nonempty()) pr.onResizeNote(nodeId, { x: Math.round(note.position().x), y: Math.round(note.position().y), w: Number(note.data('w')), h: Number(note.data('h')) });
      } else if (role === 'label') {
        const item = cy.getElementById(nodeId);
        if (item.empty()) return;
        // Abstand zur Marke (ganzzahlig gespeichert)
        const dy = Math.round(p.y - item.position().y);
        ele.position({ x: item.position().x, y: item.position().y + dy });
        if (dy !== Number(ele.data('ldy'))) {
          ele.data('ldy', dy);
          pr.onMoveLabel(nodeId, dy);
        }
      } else if (role === 'flag') {
        const tl = timelineOf(nodeId);
        if (!tl?.timeline) return;
        const flagId = String(ele.id()).split(':').slice(2).join(':');
        const kind = flagId === 'start' ? 'start' : flagId === 'end' ? 'end' : 'user';
        let pos = axisAt(tl, p.x);
        if (kind === 'user') {
          pos = Math.min(Math.max(pos, tl.timeline.startPos), tl.timeline.endPos);
          return pr.onMoveFlag(tl.id, flagId, kind, pos, Math.round(p.y - Number(ele.data('dockY'))));
        }
        // Start und Ende dürfen die Elemente nicht abschneiden und müssen Abstand halten
        const extent = itemsExtent(propsRef.current.ctx, tl.id);
        if (kind === 'start') pos = Math.min(pos, extent ? extent.min : Infinity, tl.timeline.endPos - 100);
        else pos = Math.max(pos, extent ? extent.max : -Infinity, tl.timeline.startPos + 100);
        pr.onMoveFlag(tl.id, flagId, kind, pos);
      }
    });

    // Hover-Nachricht: Titel, Klasse, Zeit und Kurzbeschreibung; die Ränder eines Zeitraums zeigen nur ihre Zeit
    cy.on('mouseover', 'node', (e) => {
      const ele = e.target as cytoscape.NodeSingular;
      const role = ele.data('role') as string;
      if (role === 'event' || role === 'period' || role === 'card' || role === 'label') propsRef.current.onHoverItem?.(String(ele.data('nodeId')));
      const nodeId = String(ele.data('nodeId'));
      // Karte, Vorschaukarte und die Inhaltskacheln darin (sie gehören zur Karte): Übersichtsfenster statt Hover-Nachricht, wenn es eines gibt
      if (role === 'card' || role === 'label' || role === 'content') {
        const host = cy.container()!.getBoundingClientRect();
        const bb = ele.renderedBoundingBox({ includeLabels: false });
        const box = { left: host.left + bb.x1, top: host.top + bb.y1, right: host.left + bb.x2, bottom: host.top + bb.y2 };
        if (propsRef.current.onHoverPreview?.(nodeId, box)) return;
      }
      // Notizzettel zeigen ihren Text selbst
      if (role === 'axis' || role === 'flag' || role === 'flagpin' || role === 'content' || role === 'note' || role === 'grip' || role === 'notepin' || role === 'blur') return;
      const node = propsRef.current.model.nodes.find((n) => n.id === (role === 'handle' ? ele.id() : nodeId));
      if (!node) return;
      const p = ele.renderedPosition();
      if (role === 'handle') {
        if (node.when) setTooltip({ x: p.x + 14, y: p.y + 18, title: node.title, typeName: node.meta?.edge === 'from' ? 'Beginn' : 'Ende', when: node.when, text: '' });
        return;
      }
      setTooltip({ x: p.x + 16, y: p.y + 16, title: node.title, typeName: `${node.meta?.background ? de.background.kind : node.typeName}${node.issues ? ` · ⚠ ${de.issues.dot(node.issues)}` : ''}`, when: node.when, text: node.hover || (node.when ? '' : '–') });
    });
    const hoverOut = (): void => {
      setTooltip(null);
      propsRef.current.onHoverItem?.(null);
      propsRef.current.onHoverPreview?.(null, null);
    };
    cy.on('mouseout', 'node', hoverOut);
    // Maus verlässt die Fläche (auch mit einem schnellen Sprung, dann meldet Cytoscape kein mouseout): nichts ist mehr überfahren.
    // Das Übersichtsfenster bleibt außen vor – es liegt über der Fläche und schließt sich selbst, wenn die Maus es verlässt.
    const hoverHost = cy.container()!;
    const leaveHost = (): void => {
      setTooltip(null);
      propsRef.current.onHoverItem?.(null);
    };
    hoverHost.addEventListener('mouseleave', leaveHost);

    runFocus.current = (f, animate) => {
      /**
       * Marke bzw. Karte, beim Rahmen samt Vorschaukarte. Nur falls nötig zählen die Marken: Vorschaukarten halten ihren Abstand in
       * Bildschirmpixeln und liegen nach einem Zoom anders – sonst rückte die Fläche nach jedem Rahmen beim nächsten Klick noch einmal.
       */
      const boxOf = (ids: string[], cards: boolean): Box | null => {
        const eles = cy.collection();
        for (const id of ids) eles.merge(cy.getElementById(id)).merge(cards ? cy.getElementById(`label:${id}`) : cy.collection());
        return eles.nonempty() ? eles.boundingBox({ includeLabels: false, includeOverlays: false }) : null;
      };
      const frame = f.mode === 'frame';
      const scene = boxOf([f.id], frame);
      if (!scene) return;
      const around = boxOf([f.id, ...neighborsIn(canvasSceneOrder(propsRef.current.ctx, propsRef.current.model), f.id)], frame) ?? scene;
      const view = { w: cy.width(), h: cy.height() };
      const next = frame ? frameCamera(view, scene, around) : nearCamera(view, { zoom: cy.zoom(), pan: cy.pan() }, around);
      if (!next) return;
      cy.stop();
      if (animate) cy.animate({ zoom: next.zoom, pan: next.pan }, { duration: CAMERA_MS, easing: 'ease-in-out-cubic' });
      else cy.viewport({ zoom: next.zoom, pan: next.pan });
    };
    // Solange der Nutzer die Ansicht nicht selbst bewegt hat, bleibt sie eingepasst – auch wenn sich die Fläche nach dem Öffnen
    // noch in der Größe ändert (Seitenleiste erscheint, Schriften laden).
    let userMoved = false;
    const markMoved = (): void => {
      userMoved = true;
    };
    containerRef.current.addEventListener('pointerdown', markMoved);
    containerRef.current.addEventListener('wheel', markMoved, { passive: true });
    const ro = new ResizeObserver(() => {
      cy.resize();
      // kurz nach einem Fokus (die Seitenleiste klappt dabei auf): ihn wiederholen; danach gilt die Ansicht als bewusst gesetzt
      const last = lastFocus.current;
      if (last && Date.now() - last.at < 2000) return runFocus.current(last.f, false);
      if (!userMoved && !last && cy.nodes().length > 0) fitCamera(cy);
    });
    ro.observe(containerRef.current);

    // Theme-Wechsel (hell/dunkel)
    const onTheme = (): void => {
      cy.style(buildStyle(readTheme()));
      setTheme(readTheme());
    };
    // Theme-Wechsel: System oder Knopf in der Kopfleiste
    const offTheme = onSchemeChange(onTheme);

    return () => {
      cancelAnimationFrame(raf);
      offTheme();
      wheelHost.removeEventListener('wheel', onWheel);
      hoverHost.removeEventListener('mouseleave', leaveHost);
      window.removeEventListener('mouseup', endTileDrag);
      window.removeEventListener('pointermove', trackPointer);
      window.removeEventListener('mousemove', trackPointer);
      ro.disconnect();
      cy.destroy();
      cyRef.current = null;
      known.clear();
      cache.clear();
    };
  }, []);

  // --- Elemente synchronisieren
  const { model } = props;
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    const themeKey = `${theme.surface}|${theme.text}|${theme.border}`;
    const known = sigs.current;
    const ids = new Set(model.nodes.map((n) => n.id));
    for (const v of model.nodes) if (v.kids.length > 0 && (v.role === 'card' || v.role === 'event' || v.role === 'period')) ids.add(`content:${v.id}`);
    const edgeIds = new Set(model.edges.map((e) => e.id));
    cy.batch(() => {
      cy.nodes().forEach((n) => {
        if (!ids.has(n.id())) {
          cy.remove(n);
          known.delete(n.id());
          built.current.delete(n.id());
          layoutsRef.current.delete(n.id());
        }
      });
      cy.edges().forEach((e) => {
        if (!edgeIds.has(e.id())) cy.remove(e);
      });
      for (const v of model.nodes) {
        const sig = signature(v, assetVersion, themeKey);
        const ele = cy.getElementById(v.id);
        const key = `${sig}|${JSON.stringify([v.nodeId, v.typeName, v.meta?.docked?.timelineId, v.meta?.docked?.y, v.meta?.labelDy])}`;
        let b = built.current.get(v.id);
        if (!b || b.key !== key) {
          const { data, classes } = elementData(v, theme);
          b = { key, data, classes, content: contentFor(v, { w: Number(data.w), h: Number(data.h) }, theme) };
          built.current.set(v.id, b);
        }
        const { data, classes, content } = b;
        const owner = { x: v.x, y: v.y, w: Number(data.w), h: Number(data.h) };
        const cid = `content:${v.id}`;
        const contentEle = cy.getElementById(cid);
        if (!content) {
          if (contentEle.nonempty()) cy.remove(contentEle);
          layoutsRef.current.delete(cid);
        } else {
          layoutsRef.current.set(cid, content.layout);
          const csig = JSON.stringify([sig, owner.w, owner.h]);
          if (contentEle.empty()) {
            cy.add({ group: 'nodes', data: { id: cid, ...content.data }, position: contentPos(owner, content), classes: 'content', grabbable: false });
            known.set(cid, csig);
          } else {
            if (known.get(cid) !== csig) {
              contentEle.data(content.data);
              known.set(cid, csig);
            }
            if (!cy.getElementById(v.id).grabbed()) contentEle.position(contentPos(owner, content));
          }
        }
        if (ele.empty()) {
          cy.add({ group: 'nodes', data: { id: v.id, ...data }, position: { x: v.x, y: v.y }, classes, grabbable: v.drag !== 'none' });
          known.set(v.id, sig);
          continue;
        }
        if (!ele.grabbed()) {
          const p = ele.position();
          if (Math.abs(p.x - v.x) > 0.01 || Math.abs(p.y - v.y) > 0.01) ele.position({ x: v.x, y: v.y });
        }
        if (known.get(v.id) !== sig) {
          ele.data(data);
          ele.classes(classes);
          ele.removeScratch('_measuredFs'); // Beschriftung neu ausmessen (siehe relabel)
          known.set(v.id, sig);
        }
        // Zeitstrahl und Spur, an denen ein Element hängt, ändern sich auch ohne neue Darstellung (Zeitlinie verschoben, andere Spur)
        if (ele.data('dockY') !== data.dockY || ele.data('dockTl') !== data.dockTl) ele.data({ dockY: data.dockY, dockTl: data.dockTl });
        if (ele.grabbable() !== (v.drag !== 'none')) {
          if (v.drag !== 'none') ele.grabify();
          else ele.ungrabify();
        }
      }
      for (const e of model.edges) {
        const ele = cy.getElementById(e.id);
        if (ele.empty()) {
          if (cy.getElementById(e.source).nonempty() && cy.getElementById(e.target).nonempty()) {
            cy.add({ group: 'edges', data: { id: e.id, source: e.source, target: e.target, label: e.label, lc: e.color ?? '' }, classes: e.flagLine ? (e.color ? 'flagline previewline' : 'flagline') : '' });
          }
        } else if (ele.data('label') !== e.label) ele.data('label', e.label);
      }
    });
    relabel(cy);
    if (firstFit.current && model.nodes.length > 0) {
      firstFit.current = false;
      fitCamera(cy);
      const level = lodFor(cy.zoom());
      if (level !== lodRef.current) {
        lodRef.current = level;
        props.onZoomLevel?.(level);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, theme, assetVersion]);

  // --- Auswahl hervorheben
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    cy.batch(() => {
      cy.elements().removeClass('sel');
      const picked = new Set([props.selectedId, ...(props.alsoSelected ?? [])]);
      cy.nodes().filter((n) => picked.has(String(n.data('nodeId'))) && n.data('role') !== 'handle').addClass('sel');
      if (props.selectedConnectionId) cy.getElementById(props.selectedConnectionId).addClass('sel');
    });
  }, [props.selectedId, props.alsoSelected, props.selectedConnectionId, model]);

  // --- Filter über den Graphen: nicht passende Szenen (samt ihren Linien) blass, passende leicht hervorgehoben
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    const f = props.filter;
    cy.batch(() => {
      cy.elements().removeClass('dim hit');
      if (!f) return;
      cy.nodes().forEach((n) => {
        const id = String(n.data('nodeId'));
        const role = n.data('role') as string;
        if (f.out.has(id)) n.addClass('dim');
        else if (f.hits.has(id) && role !== 'handle' && role !== 'content') n.addClass('hit');
      });
      cy.edges().forEach((e) => {
        if (e.source().hasClass('dim') || e.target().hasClass('dim')) e.addClass('dim');
      });
    });
  }, [props.filter, model]);

  // --- Kamera
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy || props.fitTick <= 0) return;
    if (!props.fitAll) return fitCamera(cy, 'view');
    // „Alles zeigen“ gleitet: Ziel ausrechnen, dann von hier aus hinfahren
    const from = { zoom: cy.zoom(), pan: { ...cy.pan() } };
    fitCamera(cy, 'all');
    const to = { zoom: cy.zoom(), pan: { ...cy.pan() } };
    cy.viewport(from);
    cy.animate(to, { duration: CAMERA_MS, easing: 'ease-in-out-cubic' });
  }, [props.fitTick]); // eslint-disable-line react-hooks/exhaustive-deps -- nur ein neuer fitTick passt ein

  // --- Fokus auf eine Szene (PF 11): weich auf sie und ihre Nachbarn in der Weltzeit fahren (nach dem ersten Einpassen, im nächsten Bild)
  useEffect(() => {
    const f = props.focus;
    if (!cyRef.current || !f) return;
    lastFocus.current = { f, at: Date.now() };
    const raf = requestAnimationFrame(() => runFocus.current(f, true));
    return () => cancelAnimationFrame(raf);
  }, [props.focus?.tick]); // eslint-disable-line react-hooks/exhaustive-deps -- nur ein neuer Auftrag fährt

  return (
    <div className="canvas-host">
      <div ref={containerRef} className="canvas" data-testid="graph-canvas" />
      {dropHint && (
        <div className={`canvas-hint drop-hint ${dropHint.ok ? 'ok' : 'no'}`} role="status" data-testid="drop-hint" data-ok={dropHint.ok}>
          {dropHint.ok ? '✓ ' : '✕ '}
          {dropHint.text}
        </div>
      )}
      {dragFlag && (
        <div className={`drag-time-flag ${dragFlag.edge}`} style={{ left: dragFlag.x, top: dragFlag.y }} role="status" data-testid="drag-time-flag">
          <span className="drag-time-box">{dragFlag.text}</span>
        </div>
      )}
      {consequence && <ConsequenceList lines={consequence.lines} className="drag-consequences" style={{ left: consequence.x, top: consequence.y }} testId="drag-consequences" />}
      {carryGhost && (
        <div className="carry-ghost" style={{ left: carryGhost.x, top: carryGhost.y, ['--ghost' as string]: carryGhost.color }} data-testid="carry-ghost" data-kind={carryGhost.kind}>
          <span className="carry-icon">{iconFor(carryGhost.kind) ? <img src={iconFor(carryGhost.kind)!} alt="" width={18} height={18} /> : carryGhost.kind === 'event' ? '◆' : '▬'}</span>
          <span className="carry-title">{carryGhost.title}</span>
        </div>
      )}
      {ghost && (
        <div className="tile-ghost" style={{ left: ghost.x + 14, top: ghost.y + 14, borderColor: ghost.color }} data-testid="tile-ghost">
          {ghost.title}
        </div>
      )}
      {tooltip && (
        <div className="tooltip" style={{ left: tooltip.x, top: tooltip.y }} role="tooltip">
          <div className="t-title">{tooltip.title}</div>
          <div className="muted small">{tooltip.typeName}</div>
          {tooltip.when && (
            <div className="small" data-testid="tooltip-when">
              {tooltip.when}
            </div>
          )}
          {tooltip.text && <div>{tooltip.text}</div>}
        </div>
      )}
    </div>
  );
}
